<script lang="ts">
import { tv } from 'tailwind-variants';

const theme = tv({
	slots: {
		root: 'overflow-clip ease-out motion-safe:transition-all',
		sheet: '',
	},
	variants: {
		dynamicWidth: {
			true: { root: 'w-(--sheet-width)', sheet: 'w-max' },
		},
		dynamicHeight: {
			true: { root: 'h-(--sheet-height)', sheet: 'h-max' },
		},
	},
});
</script>

<script setup lang="ts">
import { useResizeObserver } from '@vueuse/core';
import { computed, useTemplateRef } from 'vue';

const props = withDefaults(defineProps<{
	class?: string;
	dimension?: 'width' | 'height' | 'both';
	/**
	 * Maintains the maximum reached size of the sheet along this dimension, even if the content changes.
	 */
	maintainMaxSize?: 'width' | 'height' | 'both';
	duration?: number;
	ui?: Partial<typeof theme['slots']>;
}>(), {
	dimension: 'both',
	duration: 50,
});
const wrapperRef = useTemplateRef('wrapper');
const sizerRef = useTemplateRef('sizer');

const ui = computed(() => theme({
	dynamicWidth: props.dimension === 'width' || props.dimension === 'both',
	dynamicHeight: props.dimension === 'height' || props.dimension === 'both',
}));

let maxHeight = 0;
let maxWidth = 0;

useResizeObserver(sizerRef, () => {
	const height = Math.max(sizerRef.value!.offsetHeight, maxHeight);
	const width = Math.max(sizerRef.value!.offsetWidth, maxWidth);
	wrapperRef.value!.style.setProperty('--sheet-height', `${height.toFixed(1)}px`);
	wrapperRef.value!.style.setProperty('--sheet-width', `${width.toFixed(1)}px`);

	if (props.maintainMaxSize === 'height' || props.maintainMaxSize === 'both') {
		maxHeight = height;
	}

	if (props.maintainMaxSize === 'width' || props.maintainMaxSize === 'both') {
		maxWidth = width;
	}
});
</script>

<template>
	<div
		ref="wrapper"
		:class="ui.root({ class: props.ui?.root })"
		:style="{ transitionDuration: `${props.duration}ms` }"
	>
		<div
			ref="sizer"
			:class="ui.sheet({ class: [props.ui?.sheet, props.class] })"
			v-bind="$attrs"
		>
			<slot />
		</div>
	</div>
</template>
